import { forwardRef, ReactNode } from 'react';
import AnimateOnViewport from 'app/(web)/components/common/AnimateOnViewport';
import { Flex } from 'designSystem/Layouts/Layouts';
import { twMerge } from 'tailwind-merge';

type ButtonTypes =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'white'
  | 'whiteDerma'
  | 'derma'
  | 'dermaDark'
  | 'disabledGrey'
  | 'disabled';

export type ButtonSizes = 'sm' | 'md' | 'lg' | 'xl';

type ButtonProps = {
  as?: 'button' | 'div';
  type?: ButtonTypes;
  size?: ButtonSizes;
  href?: string;
  className?: string;
  customStyles?: string;
  wrapperClassName?: string;
  onClick?: (...args: any[]) => void;
  children: ReactNode;
  disabled?: boolean;
  isAnimated?: boolean;
  origin?: 'top' | 'right' | 'bottom' | 'left';
  id?: string;
  [key: string]: any;
};

export const Button = forwardRef<any, ButtonProps>(
  (
    {
      as = 'button',
      type = 'primary',
      size = 'md',
      href = '',
      className = '',
      customStyles = '',
      wrapperClassName = '',
      onClick,
      children,
      disabled = false,
      isAnimated = false,
      origin = 'bottom',
      id = '',
      ...rest
    },
    ref
  ) => {
    const ButtonWrapper = href ? 'a' : as === 'button' ? 'button' : 'div';
    const disabledStyles = disabled ? 'cursor-default pointer-events-none' : '';
    const wrapperClasses = twMerge(
      `relative overflow-visible inline-block ${className} ${disabledStyles}`
    );

    const buttonElement = (
      <ButtonWrapper
        {...(href ? { href } : {})}
        id={id}
        target={rest?.target}
        onClick={onClick}
        type={rest?.isSubmit ? 'submit' : 'button'}
        className={wrapperClasses}
        ref={ref}
        {...rest}
      >
        <ButtonBody
          type={type}
          size={size}
          customStyles={customStyles}
          disabled={disabled}
          {...rest}
        >
          {children}
        </ButtonBody>
      </ButtonWrapper>
    );

    return isAnimated ? (
      <AnimateOnViewport origin={origin} className={wrapperClassName}>
        {buttonElement}
      </AnimateOnViewport>
    ) : (
      buttonElement
    );
  }
);

Button.displayName = 'Button';

const ButtonBody = ({
  type,
  size,
  customStyles,
  disabled = false,
  children,
  id,
  ...rest
}: {
  type: ButtonTypes;
  size: ButtonSizes;
  customStyles?: string;
  disabled?: boolean;
  children: ReactNode;
  id?: string;
}) => {
  const DISABLED_STYLES =
    'cursor-default pointer-events-none border-none bg-white text-hg-black300 hover:bg-hg-black100 hover:text-hg-black300 active:bg-hg-black100 active:text-hg-black300';

  const STYLES: any = {
    common: 'transition-all relative text-center rounded-full',
    primary: 'bg-hg-secondary text-white hover:bg-hg-secondary700',
    secondary:
      'bg-hg-secondary100 text-hg-secondary border border-hg-secondary active:bg-hg-secondary300 hover:bg-hg-secondary300',
    tertiary:
      'bg-hg-primary text-hg-black border border-hg-black active:bg-hg-secondary100 hover:bg-hg-secondary100',
    derma: `bg-derma-primary text-white ${
      !disabled
        ? 'hover:bg-derma-primary500 active:bg-derma-primary500 hover:text-derma-primary'
        : ''
    }`,
    dermaDark: `bg-derma-tertiary text-white ${
      !disabled
        ? 'hover:bg-derma-tertiaryDark active:bg-derma-tertiaryDark'
        : ''
    }`,
    white:
      'bg-white text-hg-black border border-hg-black hover:bg-hg-secondary100 active:bg-hg-secondary100',
    whiteDerma:
      'bg-white text-hg-black border border-hg-black hover:bg-derma-primary100/50 active:bg-derma-primary100/50',
    disabled:
      'bg-white text-hg-black300 hover:bg-hg-black100 hover:text-hg-black300 cursor-default pointer-events-none',
    disabledGrey:
      'bg-hg-black100 text-hg-black300 hover:bg-hg-black100 hover:text-hg-black400 cursor-default pointer-events-none',
    sm: 'text-xs font-medium h-[32px] px-4',
    md: 'text-xs font-medium h-[40px] px-4',
    lg: 'text-md font-semibold h-[48px] px-6',
    xl: `${
      type === 'primary' ? 'h-[64px] text-lg' : 'h-[60px] text-md'
    } font-semibold px-6`,
  };

  const styles = twMerge(
    `${STYLES.common} ${STYLES[type]} ${STYLES[size]} ${customStyles} ${
      disabled ? DISABLED_STYLES : ''
    }`
  );

  return (
    <Flex layout="row-center" className={styles} id={id} {...rest}>
      {children}
    </Flex>
  );
};
